import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Droplets,
  Leaf,
  Snowflake,
  ArrowRight,
  MessageCircle,
  ShieldCheck,
} from "lucide-react";
import { MESSENGER_URL } from "@/constants/content";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };

const INGREDIENTS = [
  { text: "1 dl-es fújós flakon" },
  { text: "80 ml desztillált víz" },
  { text: "1 teáskanál mandula- vagy jojobaolaj" },
  { text: "összesen 25 csepp illóolaj – narancs, levendula, menta, citrom vagy ezek keveréke" },
];

const OILS = [
  {
    name: "Narancs",
    tag: "Napfényes derű",
    text: "Édes, meleg illat, ami pillanatok alatt oldja a feszültséget és emeli a hangulatot.",
    accent: "#D4AF37",
  },
  {
    name: "Levendula",
    tag: "Belső csend",
    text: "A klasszikus nyugtató. Puha lélegzetvétel, elengedett vállak, tisztább fej.",
    accent: "#7C6AA8",
  },
  {
    name: "Menta",
    tag: "Éberség",
    text: "Hűs, tiszta lélegzet. Kiváló autóba, tanuláshoz, délutáni holtponton.",
    accent: "#2A9D8F",
  },
  {
    name: "Citrom",
    tag: "Frissesség",
    text: "Élénkítő, tisztító, koncentrációt segítő – tavaszi zápor egy flakonban.",
    accent: "#A88A2B",
  },
];

export default function AromaFlowRecept() {
  return (
    <div data-testid="aroma-flow-recept-page" className="text-[#1A2825]">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-10">
        <motion.div initial="hidden" animate="show" variants={fadeUp}>
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#A88A2B]">
            <Sparkles className="h-3.5 w-3.5" /> Aroma Flow · Ajándék recept
          </span>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#1A2825] mt-4 leading-[1.05]">
            Illóolajos <em className="not-italic text-[#2A9D8F]">nyugalom-spray</em>
          </h1>
          <p className="mt-6 text-lg text-[#3A4D4A] font-light leading-relaxed">
            Egy egyszerű, mégis varázslatos recept, amit egy perc alatt összekeversz. Vidd magaddal
            az autóba, tedd az íróasztalra, vagy fújd a párnád köré lefekvés előtt – és engedd, hogy
            a levegő maga simogasson meg.
          </p>
          <p className="mt-4 text-[#A88A2B] italic font-light">
            A harmónia belül kezdődik és kívül látszik.
          </p>
        </motion.div>
      </section>

      {/* Hozzávalók */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-12">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-2xl bg-[#2A9D8F]/10 border border-[#2A9D8F]/25 flex items-center justify-center">
            <Droplets className="h-5 w-5 text-[#2A9D8F]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#A88A2B]">Hozzávalók</div>
            <h2 className="font-serif-display text-2xl md:text-3xl leading-snug">Mit készíts elő?</h2>
          </div>
        </div>
        <div
          data-testid="aroma-ingredients"
          className="bg-white rounded-[1.75rem] border border-[#EAE5D9] p-7 md:p-9 shadow-[0_8px_28px_rgba(26,40,37,0.04)]"
        >
          <ul className="space-y-4">
            {INGREDIENTS.map((it, i) => (
              <li key={i} className="flex gap-4 items-start">
                <span className="font-serif-display text-2xl text-[#2A9D8F] leading-none shrink-0 mt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="text-base md:text-lg text-[#1A2825] font-light leading-relaxed">
                  {it.text}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Használat + Tárolás */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-14">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 md:grid-cols-2 gap-5"
        >
          <motion.div
            variants={fadeUp}
            data-testid="aroma-usage-card"
            className="bg-[#F6F3EB] rounded-[1.75rem] border border-[#EAE5D9] p-7"
          >
            <div className="flex items-center gap-2">
              <span className="h-9 w-9 rounded-xl flex items-center justify-center border border-[#A88A2B]/40 bg-[#D4AF37]/12 text-[#A88A2B]">
                <Sparkles className="h-4 w-4" />
              </span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#A88A2B]">
                Használat
              </span>
            </div>
            <h3 className="font-serif-display text-xl md:text-2xl leading-snug text-[#1A2825] mt-4">
              Rázd fel, lélegezd be
            </h3>
            <p className="mt-3 text-sm md:text-base text-[#3A4D4A] font-light leading-relaxed">
              Használat előtt <span className="font-medium text-[#1A2825]">mindig jól rázd fel</span>{" "}
              a flakont, hogy az olajcseppek szépen eloszoljanak. Fújj néhányat a levegőbe magad
              körül, a párnahuzatra, a kocsi levegőjébe, vagy a csuklód belső oldalára.
            </p>
          </motion.div>

          <motion.div
            variants={fadeUp}
            data-testid="aroma-storage-card"
            className="bg-white rounded-[1.75rem] border border-[#EAE5D9] p-7 shadow-[0_8px_28px_rgba(26,40,37,0.04)]"
          >
            <div className="flex items-center gap-2">
              <span className="h-9 w-9 rounded-xl flex items-center justify-center border border-[#2A9D8F]/30 bg-[#2A9D8F]/10 text-[#2A9D8F]">
                <Snowflake className="h-4 w-4" />
              </span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#2A9D8F]">
                Tárolás
              </span>
            </div>
            <h3 className="font-serif-display text-xl md:text-2xl leading-snug text-[#1A2825] mt-4">
              Hűvös, fénytől védett helyen
            </h3>
            <p className="mt-3 text-sm md:text-base text-[#3A4D4A] font-light leading-relaxed">
              Az illóolajok érzékenyek a fényre és a hőre. Tárold sötét, hűvös helyen, hogy az illat
              és a hatás hosszú heteken át megőrizze frissességét.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Illóolaj-térkép */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-14">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-2xl bg-[#1C6B61]/10 border border-[#1C6B61]/25 flex items-center justify-center">
            <Leaf className="h-5 w-5 text-[#1C6B61]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#A88A2B]">
              Illóolaj-térkép
            </div>
            <h2 className="font-serif-display text-2xl md:text-3xl leading-snug">
              Válaszd ki a hangulatot
            </h2>
          </div>
        </div>
        <p className="text-sm md:text-base text-[#3A4D4A] font-light leading-relaxed mb-8 max-w-3xl">
          A 25 csepp lehet egyetlen olaj, vagy több finoman összehangolt keveréke. Íme egy kis
          iránytű, hogy megtaláld a saját hangolásod:
        </p>
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {OILS.map((o, i) => (
            <motion.div
              key={o.name}
              variants={fadeUp}
              data-testid={`aroma-oil-card-${i}`}
              className="bg-white rounded-[1.5rem] border border-[#EAE5D9] p-6 flex flex-col shadow-[0_8px_28px_rgba(26,40,37,0.04)]"
            >
              <span
                className="text-[10px] uppercase tracking-[0.22em]"
                style={{ color: o.accent }}
              >
                {o.tag}
              </span>
              <h3 className="font-serif-display text-xl text-[#1A2825] mt-2">{o.name}</h3>
              <p className="mt-3 text-sm text-[#3A4D4A] font-light leading-relaxed">{o.text}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Biztonsági megjegyzés */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-16">
        <div
          data-testid="aroma-safety"
          className="rounded-2xl bg-[#1C6B61]/8 border border-[#1C6B61]/20 p-5 md:p-6 flex gap-3"
        >
          <ShieldCheck className="h-5 w-5 text-[#1C6B61] shrink-0 mt-0.5" />
          <p className="text-sm md:text-base text-[#1A2825] font-light leading-relaxed">
            <span className="font-medium">Jó tudni:</span> Az illóolajok koncentrált növényi
            kivonatok. Kisgyermekek, várandósok és háziállatok közelében óvatosan használd, és
            először érzékeny bőrre próbáld ki egy kis felületen.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          data-testid="aroma-cta"
          className="relative overflow-hidden rounded-[2rem] bg-[#1A2825] text-white p-8 md:p-12"
        >
          <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#2A9D8F]/20 blur-3xl" />
          <div className="absolute -bottom-20 -left-10 h-52 w-52 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">
              <Sparkles className="h-4 w-4" /> Egy szinttel mélyebb harmónia
            </span>
            <h2 className="font-serif-display text-3xl md:text-4xl font-light mt-4 leading-snug">
              Ha a levegő megtett mindent, ami tőle telik – jöhet a következő réteg
            </h2>
            <p className="mt-4 text-white/75 font-light leading-relaxed max-w-xl">
              Az aroma megnyitja a lélegzetet. Egy jól időzített masszázs elengedi a testet. A
              4Life sejtszintű tisztítás pedig belülről dolgozik – együtt teljes a kép.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={MESSENGER_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="aroma-book-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#2A9D8F] hover:bg-[#22857a] text-white px-7 py-3.5 rounded-full font-medium tracking-wide transition-all shadow-[0_10px_24px_rgba(42,157,143,0.35)] hover:-translate-y-0.5"
              >
                <MessageCircle className="h-4 w-4" /> Időpontot foglalok
              </a>
              <Link
                to="/4life"
                data-testid="aroma-4life-link"
                className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white/50 text-white px-7 py-3.5 rounded-full font-medium tracking-wide transition-all"
              >
                4Life kínálat <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
